import { Button, Card, Divider, FormControlLabel, Paper, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Stack } from '@mui/system';

import lcc_logo from "../../images/lcc_logo.png"
import { useCreateFinderMutation, useGetFinderInfoQuery } from '../../features/finder/finderSlice';
import Loading from '../../components/Loading';

const email_regex = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/
const zelle_regex = /(^\(?(\d{3})\)?[-\. ]?(\d{3})[-\. ]?(\d{4})( x\d{4})?$|[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)/

function useQuery() {
    const { search } = useLocation();
  
    return React.useMemo(() => new URLSearchParams(search), [search]);
}
  
const ErrorPrompt = ({message}) => {
    if (message === "") {
        return <></>
    }
    return <Typography sx={{textAlign: "left"}} color="#a00" variant="caption">{message}</Typography>
}

const FinderRegistrationView = () => {
    let params = useParams();
    let query = useQuery();

    var parent_rep_id = 0
    if(query.get("ref")) {
        parent_rep_id = parseInt(query.get("ref"))
    }
    const {data: commission_info, error, isLoading } = useGetFinderInfoQuery(parent_rep_id)
    const [createFinder, result] = useCreateFinderMutation()

    let [state, setState] = useState({
        "first_name": "",
        "last_name": "",
        "email": "",
        "phone": "",
        "password": "",
        "verifyPassword": "",
        "paymenttype": "",
        "routing": "",
        "account": "",
        "termsAccepted": false,
        "parent": parent_rep_id,
        "page": 0
    });

    if(isLoading || result.isLoading) {
        return <Loading />
    }

    if(error || parent_rep_id === 0) {
        return <Stack sx={{margin: 1}} spacing={2} alignItems={"center"}>
        <Paper variant="outlined" sx={{width: "100%"}}>
        <Card>
        <Stack direction="column" sx={{margin: 2}} spacing={2}>
        <center><img src={lcc_logo} alt="Left Coast Cabinets" style={{height: 256, width: 256, borderRadius: 4}} /></center>
        <Typography variant='h2'>Finder’s Fee Registration</Typography>
        <p>The referral code could not be found. This may mean the referral link you're using has expired.</p>
        </Stack>
        </Card>
        </Paper>
        </Stack>;
    }

    // data sent?
    if (result.isSuccess) {
        return <Stack sx={{margin: 1}} spacing={2} alignItems={"center"}>
        <Paper variant="outlined" sx={{width: "100%"}}>
        <Card>
        <Stack direction="column" sx={{margin: 2}} spacing={2}>
        <center><img src={lcc_logo} alt="Left Coast Cabinets" style={{height: 256, width: 256, borderRadius: 4}} /></center>
        <Typography variant='h2'>Finder’s Fee Registration</Typography>
        <p>Account created!</p><Button component={Link} to="/login" variant="contained">Click here to log in now</Button>
        </Stack>
        </Card>
        </Paper>
        </Stack>;
    }

    let nameError = (state.first_name === "" || state.last_name === "") ? "First and last name required" : ""
    let phoneError = state.phone === "" ? "Phone number required" : ""
    let paymentError = state.paymenttype === "" ? "Payment type required" : (state.account === "" ? "Payment account required" : (state.paymenttype === "Zelle" ?
            (!zelle_regex.test(state.account) ? "Invalid Zelle account - must be either a valid e-mail address or a US phone number" : "")
            : (state.paymenttype === 'ACH Direct Deposit' ? (state.account === "" || state.routing === "" ? "Must provide routing and account numbers" : "") :
            (!email_regex.test(state.account) ? "Account must be a valid e-mail address" : ""))))
    let emailError = state.email.length === 0 ? "E-mail required" : (!email_regex.test(state.email) ? "Invalid e-mail address" : "")
    let passwordError = state.password.length === 0 ? "Password required" : (state.password.length < 8 ? "Password too short" : (state.password !== state.verifyPassword ? "Passwords don't match" : ""))
    let termsError = !state.termsAccepted ? "Terms must be accepted" : ""
    let percentage = commission_info.finder_commission

    let page1issues = nameError || emailError || passwordError
    let page2issues = termsError
    let issues = nameError || paymentError || emailError || passwordError || termsError

    let termsAndConditions = <><p>This agreement is between Left Coast Cabinets and {state.name === "" ? "you" : state.name}. By clicking "I Agree" below you accept these terms and conditions.</p><ol><li>Left Coast Cabinets has the right to calculate and determine the commission per order.
        The method used for determining the commission is the order total before taxes and shipping costs multiplied by the commission percentage ({percentage}%).</li><li>
        If a customer does not use the discount code supplied by the finder, no commission will be granted.</li>
        <li>The discount code will work for 180 days from creation. During the last 30 days, another extension can be requested through the interface.</li>
        <li>Commissions are paid out at the end of each month for that month. The actual time it takes for payouts to reach your account depends on banking schedules and the payment method used.</li>
        </ol>
    </>

    let terms = (state.termsAccepted ?
        <Stack spacing={2}><div>Terms accepted.</div><Button onClick={(e) => {setState({...state, termsAccepted: false})}} variant="outlined">Review</Button></Stack>
        :
        <Stack spacing={2}><div>{termsAndConditions}</div><Button onClick={(e) => {setState({...state, termsAccepted: true})}} variant="outlined">I agree</Button></Stack>
    )

    const register = e => {
        createFinder(state);
    }

    var pages = [];
    pages.push(
        <Stack direction="column" sx={{margin: 2}} spacing={2}>
            <center><img src={lcc_logo} alt="Left Coast Cabinets" style={{height: 256, width: 256, borderRadius: 4}} /></center>
            <Typography variant='h2'>Finder’s Fee Registration</Typography>
            <Stack direction="row" spacing={2}>
            <TextField sx={{width: "40%"}} error={state.first_name === ""} label="First name" value={state.first_name} onChange={e => setState({...state, first_name: e.target.value})} />
            <TextField sx={{width: "60%"}} error={state.last_name === ""} label="Last name" value={state.last_name} onChange={e => setState({...state, last_name: e.target.value})} />
            </Stack>
            <ErrorPrompt message={nameError} />
            <TextField key="phone" error={phoneError !== ""} label="Phone number" value={state.phone} onChange={e => setState({...state, phone: e.target.value})} />
            <ErrorPrompt message={phoneError} />
            <TextField key="username" error={emailError !== ""} label="Email" value={state.email} onChange={e => setState({...state, email: e.target.value})} />
            <ErrorPrompt message={emailError} />
            <TextField key="password" error={passwordError !== ""} label="Password" type="password" value={state.password} onChange={e => setState({...state, password: e.target.value})} />
            <TextField key="verify_password" error={passwordError !== ""} label="Verify password" type="password" value={state.verifyPassword} onChange={e => setState({...state, verifyPassword: e.target.value})} />
            <ErrorPrompt message={passwordError} />
            <Button type="next" variant="contained" onClick={(e) => {setState({...state, page: state.page + 1})}} disabled={page1issues !== ""}>Next</Button>
        </Stack>
    )
    pages.push(
        <Stack direction="column" sx={{margin: 2}} spacing={2}>
            <Stack direction="row" sx={{display: "flex", alignItems: "center", width: "100%"}} spacing={2} justifyContent={"center"}><img src={lcc_logo} alt="Left Coast Cabinets" style={{height: 64, borderRadius: 4}} />
            <Typography variant='h2'>Finder’s Fee Registration</Typography>
            </Stack>
            <Button sx={{width: "100px"}} type="prev" variant="outlined" onClick={e => {setState({...state, page: state.page - 1})}}>Back</Button>
            <Divider textAlign='left'>Terms and conditions</Divider>
            {terms}
            <ErrorPrompt message={termsError} />
            <Button type="next" variant="contained" onClick={(e) => {setState({...state, page: state.page + 1})}} disabled={page2issues !== ""}>Next</Button>
        </Stack>
    )
    var payment = state.paymenttype === "" ? <><RadioGroup value={state.paymenttype} onChange={e => setState({...state, paymenttype: e.target.value})}>
            <FormControlLabel value="PayPal" control={<Radio />} label="PayPal" />
            <FormControlLabel value="Venmo" control={<Radio />} label="Venmo" />
            <FormControlLabel value="Zelle" control={<Radio />} label="Zelle" />
            <FormControlLabel value="ACH Direct Deposit" control={<Radio />} label="ACH Direct Deposit" />
        </RadioGroup>
        <ErrorPrompt message={paymentError} />
    </> : <>
        <Stack direction={"row"} spacing={2} sx={{alignItems: "center", display: "flex"}}><Typography variant='p'>{state.paymenttype}</Typography><Button variant="outlined" onClick={e => setState({...state, paymenttype: ""})}>Change</Button></Stack>
        {state.paymenttype === "ACH Direct Deposit" ? <>
            <TextField label="Routing number" value={state.routing} onChange={e => setState({...state, routing: e.target.value})} />
            <TextField label="Account number" value={state.account} onChange={e => setState({...state, account: e.target.value})} />
        </> : (state.paymenttype === "Zelle" ? <>
            <TextField label="Zelle email / phone number" value={state.account} onChange={e => setState({...state, account: e.target.value})} />
        </> :
            <TextField label={`${state.paymenttype} account (email)`} value={state.account} onChange={e => setState({...state, account: e.target.value})} />)}
            <ErrorPrompt message={paymentError} />
    </>

    pages.push(
        <Stack direction="column" sx={{margin: 2}} spacing={2}>
            <Stack direction="row" sx={{display: "flex", alignItems: "center", width: "100%"}} spacing={2} justifyContent={"center"}><img src={lcc_logo} alt="Left Coast Cabinets" style={{height: 64, borderRadius: 4}} />
            <Typography variant='h2'>Finder’s Fee Registration</Typography>
            </Stack>
            <Button sx={{width: "100px"}} type="prev" variant="outlined" onClick={e => {setState({...state, page: state.page - 1})}}>Back</Button>
            <Divider textAlign='left'>Payout account</Divider>
            {payment}
            <Button type="submit" variant="contained" onClick={register} disabled={issues !== ""}>Register</Button>
        </Stack>
    )
    return <Stack sx={{margin: 1}} spacing={2} alignItems={"center"}>
        <Paper variant="outlined" sx={{width: "100%"}}>
        <Card>
        {pages[state.page]}
        </Card>
        </Paper>
    </Stack>;
}

export default FinderRegistrationView;
